import React, { useState, useEffect } from 'react';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/card';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const MyAlertsPage = () => {
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchAlerts();
    }
  }, [activeBusiness?.id]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/part5/intelligence/me/alerts');
      setAlerts(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to load alerts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle className="w-6 h-6 text-red-500" />;
      case 'WARN':
        return <AlertTriangle className="w-6 h-6 text-orange-500" />;
      default:
        return <Info className="w-6 h-6 text-blue-500" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-50 border-red-200';
      case 'WARN':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-700';
      case 'WARN':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading your alerts...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Alerts</h1>
        <p className="text-gray-600 mt-1">Notifications and important updates for you</p>
      </div>

      {/* Summary Cards */}
      {alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <p className="text-sm text-gray-600">Total Alerts</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{alerts.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-600">Critical</p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {alerts.filter(a => a.severity === 'CRITICAL').length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-600">Warnings</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">
              {alerts.filter(a => a.severity === 'WARN').length}
            </p>
          </Card>
        </div>
      )}

      {/* Alerts List */}
      <div className="space-y-3">
        {alerts.length > 0 ? (
          alerts.map((alert) => (
            <Card key={alert.id} className={`p-4 border-2 ${getSeverityColor(alert.severity)}`}>
              <div className="flex items-start gap-4">
                {getSeverityIcon(alert.severity)}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${getSeverityBadge(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    <span className="text-xs text-gray-500">
                      {alert.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  
                  <p className="text-gray-900 font-medium mb-2">{alert.message}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                    {alert.status === 'ACKNOWLEDGED' && (
                      <span className="flex items-center gap-1 text-yellow-600">
                        <CheckCircle className="w-4 h-4" />
                        Acknowledged
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-12">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
              <p className="text-gray-600">You have no active alerts. Keep up the great work! 👍</p>
            </div>
          </Card>
        )}
      </div>

      {/* Tips */}
      {alerts.length > 0 && (
        <Card className="p-6 bg-yellow-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">📢 What to do?</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Review each alert and take necessary action</li>
            <li>• If you need help, contact your manager</li>
            <li>• Check "My Performance" page to see how to improve your scores</li>
            <li>• Address critical alerts first, then warnings</li>
          </ul>
        </Card>
      )}
    </div>
  );
};

export default MyAlertsPage;